<template>
  <div :class="['main-wrapper', computedClasses]">
    <template v-if="isLoading">
      <app-header :is-show-info="false" />
      <loading />
    </template>

    <router-view v-else />

    <app-footer></app-footer>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  buildInstruction,
  buildRequest,
  getBrowserVersion,
  isOpenBankingAcquirer,
  isPostTransactionScreen,
  isRequest,
  isStringSet,
  toUpperCaseObject,
} from '@/utils'
import Loading from '@/components/Loading'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import Constants from '@/constants'
import LoadingManager from '@/LoadingManager'
import { duplicateTabMixin } from '@/mixins/duplicateTabMixin'
import SidMessage from '@/dto/SidMessage'

export default {
  name: 'Main',
  components: {
    'app-footer': Footer,
    'app-header': Header,
    Loading,
  },
  mixins: [duplicateTabMixin],

  data() {
    return {
      isLoading: true,
      persistParams: true,
    }
  },
  computed: {
    ...mapState('transaction', ['transaction', 'hideContent', 'showBodyOnly']),
    ...mapState('signalRMessage', ['message']),
    transactionId() {
      return this.$route.query.transactionId || this.transaction.id
    },
    transactionStatus() {
      return (
        this.transaction &&
        this.transaction.status &&
        this.transaction.status.toUpperCase()
      )
    },
    isTransactionError() {
      return (
        this.message.instruction.instructionType ===
          Constants.instructionTypes.CANCEL ||
        this.message.instruction.instructionType ===
          Constants.instructionTypes.RETRY
      )
    },
    isTransactionLoading() {
      return (
        this.message.instruction.instructionType ===
          Constants.instructionTypes.MESSAGE ||
        this.message.instruction.instructionType ===
          Constants.instructionTypes.APPAUTH
      )
    },
    computedClasses() {
      return {
        'full-width':
          this.showBodyOnly && !isPostTransactionScreen(this.$route.name),
        'no-shadow':
          this.showBodyOnly && isPostTransactionScreen(this.$route.name),
      }
    },
  },

  watch: {
    async 'message.request.messageId'() {
      console.log('Main/watch(/request.messageId', this.message.request)
      if (Constants.requestPages[this.message.request.requestType]) {
        this.logRequestInfo()

        await this.$router
          .push({
            name: Constants.requestPages[this.message.request.requestType],
            query: { transactionId: this.transactionId },
          })
          .catch((e) => {
            console.info(e)
          })

        LoadingManager.setLoading(false)
        //this.isShowInstruction = false
        return
      }

      const error = new Error(
        `Unsupported requestType ${this.message.request.requestType}`
      )
      this.$handleException({ error })
    },
    async 'message.instruction.messageId'() {
      console.log(
        'Main/watch/message.instruction.messageId',
        this.message.instruction
      )
      if (this.isTransactionError) {
        this.setError(this.message.instruction.message)
      }

      if (
        Constants.instructionPages[this.message.instruction.instructionType]
      ) {
        this.logInstructionInfo()

        await this.$router
          .push({
            name: Constants.instructionPages[
              this.message.instruction.instructionType
            ],
            query: { transactionId: this.transactionId },
          })
          .catch((e) => {
            console.info(e)
          })

        LoadingManager.setLoading(!!this.isTransactionLoading)
        //this.isShowInstruction = !!this.isTransactionLoading
        return
      }
      LoadingManager.setLoading(!!this.isTransactionLoading)
      //this.isShowInstruction = !!this.isTransactionLoading
      // This is to try help by throwing an error if code was written regarding the instruction that does
      // not make sense, but caused more problems.
      if (
        !this.isTransactionLoading &&
        !Constants.instructionPages[this.message.instruction.instructionType] &&
        this.message.instruction.instructionType !==
          Constants.instructionTypes.BEHAVIOURNONE
      ) {
        const error = new Error(
          `Unsupported instructionType ${this.message.instruction.instructionType}`
        )
        this.$handleException({ error })
      }
    },
    transactionStatus() {
      switch (this.transactionStatus) {
        case 'CANCELLED':
          this.openPage('cancelled')
          this.$logInfo({
            transactionId: this.transactionId,
            message: Constants.logMessages.showTransaction(
              this.transactionStatus
            ),
          })
          break
        case 'COMPLETED':
          this.openPage('completed')
          this.$logInfo({
            transactionId: this.transactionId,
            message: Constants.logMessages.showTransaction(
              this.transactionStatus
            ),
          })
          break
        case 'PROCESSING':
          this.openPage('progress')
          break
        case 'CREATED':
        case null:
          this.openPage('banks')
          this.$logInfo({
            transactionId: this.transactionId,
            message: Constants.logMessages.showTransactionCreated,
          })
          break
      }
    },
  },

  methods: {
    ...mapActions('appSettings', ['setError', 'setOriginalUrl']),
    ...mapActions('merchantSettings', ['loadSettings', 'setSettings']),
    ...mapActions('transaction', [
      'getTransaction',
      'createTransaction',
      'getTransactionReload',
    ]),
    ...mapActions('signalRMessage', [
      'setRequest',
      'setInstruction',
      'setSidMessage',
    ]),
    ...mapActions('consentMessage', ['fetchConsentMessage']),

    logInstructionInfo() {
      const type = this.message.instruction.instructionType
      let message = this.message.instruction.message
      if (type === 4) {
        message = Constants.logMessages.showCompleted
      }

      if (type === 2) {
        message = Constants.logMessages.showRetry
      }
      this.$logInfo({
        transactionId: this.transactionId,
        message,
      })
    },
    logRequestInfo() {
      const message = Constants.logRequests[this.message.request.requestType]
      if (!message) {
        return
      }
      this.$logInfo({
        transactionId: this.transactionId,
        message,
      })
    },
    openPage(page) {
      const params = this.$route.query
      let query = { transactionId: this.transactionId }
      if (this.persistParams) query = { ...query, ...params }
      this.$router
        .push({
          name: page,
          query: query,
        })
        .catch((e) => {
          console.info(e)
        })
    },
    async setUpFromTransaction(transactionId) {
      try {
        this.isLoading = true
        const { paymentSettings, transaction, message } =
          await this.getTransactionReload({ transactionId })

        this.setSettings({
          settings: paymentSettings,
          merchantCode: transaction.merchantCode,
        })
        if (message != null) {
          if (transaction.acquirerCode === Constants.acquirerCodes.AbsaPay) {
            this.setSidMessage(
              new SidMessage({
                Identifier: message.identifier,
                GroupName: message.groupName,
                RequestType: message.requestType,
                InstructionType: message.instructionType,
                Options:
                  typeof message.options === 'string'
                    ? JSON.parse(message.options)
                    : message.options,
                IsProcessing: message.isProcessing,
                Message: message.message,
              })
            )
          } else if (isRequest(message)) {
            this.setRequest(buildRequest(message, transactionId))
          } else {
            this.setInstruction(buildInstruction(message, transactionId))
          }
        }
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
      }
    },

    async setUpFromMerchant(queryParams) {
      this.persistParams = false
      try {
        this.isLoading = true
        const transaction = {
          merchantCode: queryParams.SID_MERCHANT,
          amount: queryParams.SID_AMOUNT,
          defaultReference: queryParams.SID_DEFAULT_REFERENCE || '',
          buyerReference: queryParams.SID_BUYER_REFERENCE || '',
          sellerReference: queryParams.SID_REFERENCE,
          currency: queryParams.SID_CURRENCY,
          country: queryParams.SID_COUNTRY,
          custom01: queryParams.SID_CUSTOM_01 || '',
          custom02: queryParams.SID_CUSTOM_02 || '',
          custom03: queryParams.SID_CUSTOM_03 || '',
          custom04: queryParams.SID_CUSTOM_04 || '',
          custom05: queryParams.SID_CUSTOM_05 || '',
          idNumber: queryParams.SID_ID_NUMBER || '',
          acquirerCode: queryParams.SID_ACQUIRER_CODE || '',
          theme: queryParams.SID_THEME || '',
          externalReference: queryParams.SID_EXTERNAL_REFERENCE || '',
          externalReferenceDescription:
            queryParams.SID_EXTERNAL_REFERENCE_DESCRIPTION || '',
          browser: getBrowserVersion(),
          createConsistent: queryParams.SID_CONSISTENT,
        }

        var createdTransaction = await this.createTransaction(transaction)

        await this.loadSettings({
          transactionId: createdTransaction.id,
          merchantCode: queryParams.SID_MERCHANT,
        })
        this.setOriginalUrl(window.location.href)

        if (isStringSet(transaction.acquirerCode)) {
          const routeName = isOpenBankingAcquirer(transaction.acquirerCode)
            ? 'login'
            : 'progress'

          await this.$router.push({
            name: routeName,
            query: { transactionId: createdTransaction.id },
          })
        }

        // Check if returned transaction is already cancelled, normally indicates unique reference constraint
        if (createdTransaction.status === 'CANCELLED') {
          const transactionId = this.transactionId // Below we are unable to get 'this', so get it here
          await this.$router.push({
            name: 'cancelled',
            query: { transactionId },
          })
        } else {
          // Log that PaysidV40 was loaded
          this.$logInfo({
            transactionId: this.transactionId,
            message: `Loaded PaysidV40 with build number: ${process.env.VUE_APP_BUILD_NUMBER}`,
          })
        }
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
      }
    },
  },

  created() {
    this.$configureSignalR()
    const queryParams = this.$route.query
    const queryParamsUpper = toUpperCaseObject(queryParams)
    if (this.transactionId) {
      this.setUpFromTransaction(this.transactionId)

      // Log that the page was reloaded, because we already had the transactionID
      this.$logInfo({
        transactionId: this.transactionId,
        message: 'Re-Loaded PaysidV40',
      })
    } else if (queryParamsUpper.SID_MERCHANT !== undefined) {
      this.setUpFromMerchant(queryParamsUpper)
    } else {
      // No transactionID or SID_Merchant in the url
      this.isLoading = false
      const error = new Error(
        `Fail getting url params : ${this.$route.fullPath}`
      )
      this.$handleException({ error })
    }

    this.fetchConsentMessage()
  },
}
</script>

<style scoped lang="scss">
.main-wrapper {
  width: 656px;
  background-color: #fff;
  box-shadow: 0px 5px 15px rgba(166, 166, 166, 0.3);
  border-radius: 8px;
  position: relative;

  @media screen and (max-width: $mobile-resolution) {
    width: 100%;
    height: 100%;
    border-radius: 0;
    box-shadow: none;
  }
}

.full-width {
  width: 100%;
  border-radius: 0px;
}
</style>
